import type { AnyProperty } from '@/utilities.types';
import type { ColumnRowGroupChangedEvent, GridApi, GridOptions, IRowNode, RowClassRules } from 'ag-grid-community';
import _ from 'lodash';
import { contextContent, referenceKeyToName } from '@/vantage/components/evidence_table/helpers/utilities';
import type { ExtensionColumnConfiguration, TableColumns } from '@/vantage/types/TableConfig.types';
import type { ContextSchema } from '@/vantage/types/Context.types';
import { generateIncrementingStrings, getValueFromKeyMapping, hasKey } from '@/vantage/components/helpers/utilities';
import type { AssetBehavior } from '@/vantage/assetBehaviors';
import { itemIdField } from '@/vantage/context';
import { validateGuid } from '@/utilities/utilities';
import { generateDefaultColDef } from '@/vantage/components/evidence_table/helpers/columnDefGenerator';
import { AgGridModules } from '@/core/tableUtilities/tableUtilities.types';
import { COMMON_AG_GRID_MODULES } from '@/core/tableUtilities/agGrid.constants';

export const rowHighlightCss: string[] = generateIncrementingStrings('level', '-row-styling', 0, 8);

export const defaultColDef = generateDefaultColDef();

export const baseGridOptions: GridOptions = {
  groupAggFiltering: true,
  rowSelection: 'multiple',
  enableCharts: true,
  enableRangeSelection: true,
  rowHeight: 35,
  suppressMultiRangeSelection: true,
  groupSelectsChildren: false,
  suppressHorizontalScroll: false,
  suppressAggFilteredOnly: false,
  suppressAggFuncInHeader: true,
  suppressContextMenu: true,
  rowGroupPanelShow: 'always',
  animateRows: false,
  groupDisplayType: 'multipleColumns',
};

export const modules = COMMON_AG_GRID_MODULES.concat([
  AgGridModules.ColumnsToolPanelModule,
  AgGridModules.ColumnMenuModule,
  AgGridModules.IntegratedChartsModule,
  AgGridModules.RowGroupingModule,
  AgGridModules.MultiFilterModule,
  AgGridModules.SetFilterModule,
  AgGridModules.ColumnApiModule,
  AgGridModules.RowSelectionModule,
  AgGridModules.QuickFilterModule,
  AgGridModules.NumberFilterModule,
  AgGridModules.CustomFilterModule,
  AgGridModules.PivotModule,
  AgGridModules.RowGroupingPanelModule,
  AgGridModules.ClientSideRowModelApiModule,
  AgGridModules.RenderApiModule,
  AgGridModules.ClipboardModule,
  AgGridModules.ScrollApiModule,
  AgGridModules.GridStateModule,
  AgGridModules.InfiniteRowModelModule,
  AgGridModules.ExcelExportModule,
  AgGridModules.EventApiModule,
]);

/**
 * Applies the serialized grid state to the Evidence AG-Grid Table.
 * This function sets the column state, row group columns, quick filter text,
 * and expands or selects nodes based on the provided grid state.
 */
export const applySerializedGridState = (
  api: GridApi,
  gridState: AnyProperty,
  quickFilter: string,
  workstepId: string | null,
) => {
  const isBaseView = _.isNull(workstepId);

  api.applyColumnState({ state: gridState.column, applyOrder: true });
  api.setRowGroupColumns(gridState.row?.group?.groupColIds);
  api.setGridOption('quickFilterText', quickFilter);
  api.forEachNode((node) => {
    if (node.group) {
      const isExpanded = gridState.row?.groupExpansion?.expandedRowGroupIds?.includes(node.id);
      node.setExpanded(isExpanded);
    }
    if (!isBaseView) {
      const isSelected = gridState?.row?.selection?.includes(node.id);
      node.setSelected(isSelected);
      if (isSelected) {
        api.ensureNodeVisible(node);
      }
    }
  });

  if (api.getColumnState()) {
    setTimeout(() => api.setFilterModel(gridState.filter), 0);
  }
};

/**
 * Handles the selection of nodes in the Evidence AG-Grid table when the grouping changes.
 */
export const transitionSelectionOnGrouping = (event: ColumnRowGroupChangedEvent, rawSelectedNodes: IRowNode<any>[]) => {
  const api = event.api;
  const previousSelection = rawSelectedNodes;
  if (!previousSelection || previousSelection.length === 0) {
    return;
  }

  // Collect IDs of expanded group nodes
  const expandedGroupIds: string[] = [];
  api.forEachNode((node) => {
    if (node.group && node.expanded) {
      expandedGroupIds.push(node.id!);
    }
  });

  // Collapse all rows
  api.collapseAll();

  // Re-expand only previously expanded group nodes
  expandedGroupIds.forEach((id) => {
    const node = api.getRowNode(id);
    if (node) {
      api.setRowNodeExpanded(node, true, true);
    }
  });

  const getTargetGroupNode = (node: IRowNode<any>) => {
    const nodeKeys: string[] = [];
    api.forEachNode((node) => nodeKeys.push(node.key!));
    let currentNode: IRowNode<any> | null = node;
    let targetNode: IRowNode<any> | undefined;
    while (currentNode) {
      if (currentNode.key && nodeKeys.includes(currentNode.key)) {
        api.forEachNode((node) => {
          if (node.key === currentNode?.key) targetNode = node;
        });
        return targetNode;
      }
      currentNode = currentNode.parent;
    }
  };

  previousSelection.forEach((node) => {
    if (!node.key) {
      api.setRowNodeExpanded(api.getRowNode(node.id!)!, true, true);
    } else {
      const targetNode = getTargetGroupNode(node);
      if (targetNode) {
        targetNode.setSelected(true, false, 'api');
        if (targetNode.parent) {
          api.setRowNodeExpanded(targetNode.parent, true, true);
        }
      }
    }
  });

  setTimeout(() => {
    if (!previousSelection[0].key) {
      api.ensureNodeVisible(previousSelection[0], 'middle');
    } else {
      const targetNode = getTargetGroupNode(previousSelection[0]);
      if (targetNode) {
        api.ensureNodeVisible(targetNode, 'middle');
      }
    }
  }, 0);
};

/**
 * Retrieves the column contexts based on the provided column configuration and context schemas.
 * Creates a unique list of contexts from evidence and non-evidence contexts.
 */
export const getColumnContexts = (
  columnConfig: ExtensionColumnConfiguration,
  evidenceContext: ContextSchema[],
  nonEvidenceContext: ContextSchema[],
): string[] => {
  const contextPath = contextContent(columnConfig.contextPath!);

  return Object.entries(columnConfig.kindIdentifierPairs!).reduce((acc: any[], [kind, identifier]) => {
    const columnEvidenceContext = evidenceContext
      .filter((ctx) => ctx.identifier === identifier && ctx.kind === kind)
      .map((ctx) => contextPath(ctx));
    const columnNonEvidenceContext = nonEvidenceContext
      .filter((ctx) => ctx.identifier === identifier)
      .map((ctx) => contextPath(ctx));

    return [...new Set([...acc, ...columnEvidenceContext, ...columnNonEvidenceContext, null])];
  }, []);
};

/**
 * Generates a mapping of column names to their corresponding CSS class names for row highlighting.
 */
export function getRowClassRules(classMapping: AnyProperty) {
  const rowClassRules: RowClassRules<any> = {};
  rowHighlightCss.forEach((style) => {
    rowClassRules[style] = (params) => {
      const colStyle = classMapping[referenceKeyToName(params.node.field!)];

      return colStyle === style;
    };
  });
  rowClassRules['capsule-row-styling'] = (params) => {
    return params?.node?.key === null;
  };

  return rowClassRules;
}

export const uniquePermutations = (jsonArray: any[], keys: string[], uniquenessMapping: any) => {
  const uniqueResults = new Set<string>();

  jsonArray.forEach((item) => {
    const subset: AnyProperty<boolean> = {};
    keys.forEach((key) => {
      subset[key] = hasKey(uniquenessMapping, key) ? item[uniquenessMapping[key]] : item[key];
    });

    const subsetJSON = JSON.stringify(subset);
    uniqueResults.add(subsetJSON);
  });

  return Array.from(uniqueResults).map((item) => JSON.parse(item));
};

/**
 * Recursively collects all leaf nodes from the given node.
 */
export const collectFilteredLeafNodes = (node: IRowNode<any>, recursionDepth: number): any[] => {
  if (recursionDepth > 50) {
    console.error('Recursion depth exceeded when collecting filtered leaf nodes');
    return [];
  }
  if (node.leafGroup) {
    return node.childrenAfterFilter?.map((child) => child.data) ?? [];
  } else {
    const leafNodes = [];
    const childNodes = node.childrenAfterFilter!;
    for (let i = 0; i < childNodes.length; i++) {
      leafNodes.push(...collectFilteredLeafNodes(childNodes[i], recursionDepth + 1));
    }
    return leafNodes;
  }
};

export const calculateCreationPurview = (
  gridApi: GridApi | null,
  kind: string,
  node: IRowNode<any>,
  allowablePurviewKeys: string[],
  uniquenessMapping: AnyProperty<string>,
  assetBehavior: AssetBehavior,
) => {
  if (!kind) {
    return {};
  }

  const groupedRows = gridApi
    ?.getColumnDefs()
    ?.filter((row: any) => row.rowGroupIndex !== null)
    .sort((a: any, b: any) => a.rowGroupIndex - b.rowGroupIndex)
    .map((row: any) => referenceKeyToName(row.field))
    .filter((value) => allowablePurviewKeys.includes(value));
  const groupKindIndex = groupedRows?.indexOf(kind) ?? -1;
  const creationPurviewKeys = groupedRows?.slice(0, groupKindIndex + 1);

  return Object.fromEntries(
    creationPurviewKeys
      ?.filter((key) => Object.prototype.hasOwnProperty.call(node.aggData, key))
      .map((key) => {
        const aggregationData = assetBehavior.aggregationData(key, node);
        return [key, getValueFromKeyMapping(key, aggregationData, uniquenessMapping)];
      }) ?? [],
  );
};

/**
 * Retrieves the non-evidence purviews from the grid API based on the provided purview fields and context item ID
 * mapping.
 */
export function getNonEvidencePurviews(
  gridApi: GridApi,
  purviewFields: string[],
  contextItemIdMapping: AnyProperty<string>,
) {
  const groupedColumns = gridApi
    .getColumnState()
    .filter((state) => state.rowGroupIndex !== null && state.rowGroupIndex !== undefined)
    .map((state) => state.colId)
    .filter((column) => purviewFields.includes(referenceKeyToName(column)));

  const columnValues: AnyProperty = {};

  gridApi.forEachNode((node) => {
    const columnHeader = referenceKeyToName(node.field!);
    if (node.group && groupedColumns.includes(node.field!)) {
      const value = node.aggData[itemIdField(node.field!, contextItemIdMapping)];
      if (value && validateGuid(value.toLowerCase())) {
        if (!columnValues[columnHeader]) {
          columnValues[columnHeader] = [value];
        } else if (!columnValues[columnHeader].includes(value) && value) {
          columnValues[columnHeader].push(value);
        }
      }
    }
  });

  return columnValues;
}

function decimalsForValueMagnitude(v: number): number {
  const m = Math.abs(v);
  if (m >= 10_000) return 0;
  if (m >= 1_000) return 1;
  if (m < 0.001) return 6;
  if (m < 0.1) return 4;
  if (m < 1) return 3;
  return 2;
}

function mapNumericColumns(columns: TableColumns | undefined) {
  return Object.entries(columns?.base ?? {})
    .filter((c) => c[1].meta.type === 'NUMERIC')
    .map((c) => c[0])
    .concat(
      Object.entries(columns?.extensions ?? {})
        .filter((c) => c[1].meta.type === 'NUMERIC')
        .map((c) => c[0]),
    );
}

/**
 * For each numeric column, picks the "optimal" decimals required by any
 * non-integer value in that column, using the magnitude rule.
 * If all finite values are integers (or there are no finite values), returns 0.
 *
 * Time: O(rows * cols)
 * Extra memory: O(cols)  (constant w.r.t. number of rows)
 */
export function numericColumnDecimals(
  rows: Record<string, unknown>[] | undefined,
  columns: TableColumns | undefined,
): Record<string, number> {
  if (!rows || !columns) {
    return {};
  }

  const numericColumns = mapNumericColumns(columns);
  const m = numericColumns.length;
  if (m === 0) {
    return {};
  }

  // Per-column state
  const validNumber = new Uint8Array(m); // 0/1
  const allInts = new Uint8Array(m); // 1 means still all integers
  const decSum = new Uint32Array(m); // sum of decimals
  const decCount = new Uint32Array(m); // count of non-integer values
  const numCount = new Uint32Array(m); // count of numeric values

  // Init: assume all integers until proven otherwise
  allInts.fill(1);

  // Outer loop over rows, inner over numeric columns
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    for (let j = 0; j < m; j++) {
      const v = row[numericColumns[j]] as number;
      if (typeof v === 'number' && Number.isFinite(v)) {
        validNumber[j] = 1;
        numCount[j]++;
        if (!Number.isInteger(v)) {
          decCount[j]++;
          decSum[j] += decimalsForValueMagnitude(v);
          allInts[j] = 0;
        }
      }
    }
  }

  // Build result
  const out: Record<string, number> = {};
  for (let j = 0; j < m; j++) {
    if (validNumber[j] === 0 || allInts[j] === 1) {
      out[numericColumns[j]] = 0;
    } else {
      const decPercentage = decCount[j] / numCount[j];
      const mean = decSum[j] / decCount[j];
      const fractionalPart = mean - Math.floor(mean);
      // Round up if the fractional part is greater than 5% and the percentage of non-integer values is greater than 20%
      out[numericColumns[j]] = fractionalPart >= 0.05 && decPercentage >= 0.2 ? Math.ceil(mean) : Math.round(mean);
    }
  }
  return out;
}
